/**
 * Label + casing helpers for the Structured tab (spec §7.5, §8.1 #13).
 */

export const ACRONYMS = [
  "ID", "SSN", "EIN", "DOB", "LTV", "LTC", "DTI", "ARV", "HOA", "ZIP", "URL", "US",
  "LLC", "OFAC", "FICO", "LOE", "HUD", "VOM", "VOD", "CPL", "GC", "OA", "HELOC",
  "LOC", "W9", "APN", "MEP", "HVAC", "SOW", "EA", "LF", "SF", "LS",
];
const ACR = new Map(ACRONYMS.map((a) => [a.toLowerCase(), a]));

/** "primaryAddressCity" / "is_us_document" → ["primary","address","city"]. */
export const keyWords = (key) =>
  String(key ?? "")
    .replace(/^_+/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

const capFirst = (s) => {
  const i = s.search(/[A-Za-z]/);
  if (i < 0) return s;
  return s.slice(0, i) + s.charAt(i).toUpperCase() + s.slice(i + 1);
};

/**
 * Humanize a JSON key: split camel/snake, sentence case, keep acronyms,
 * drop a parent prefix, drop a trailing "flag" on a boolean.
 */
export const humanize = (key, { parentKey, booleanLike } = {}) => {
  let w = keyWords(key);
  if (parentKey) {
    const pw = keyWords(parentKey);
    if (pw.length && w.length > pw.length && pw.every((p, i) => w[i] === p)) {
      w = w.slice(pw.length);
    }
  }
  if (booleanLike && w.length > 1 && w[w.length - 1] === "flag") w = w.slice(0, -1);
  if (!w.length) return String(key ?? "");
  const out = w.map((x) => ACR.get(x) || x).join(" ");
  return capFirst(out);
};

/**
 * Extracted text as printed often SHOUTS ("DEMO/DISP.", "HOT W TANK"). When a
 * string has an all-caps word that isn't a known acronym, lowercase it,
 * restore acronyms, capitalize the first letter. Otherwise capitalize only
 * the first letter.
 */
export const displayCase = (value) => {
  const s = String(value ?? "");
  if (!s) return s;
  const tokens = s.match(/[A-Za-z]+/g) || [];
  const shouty = tokens.some(
    (t) => t.length >= 2 && t === t.toUpperCase() && !ACR.has(t.toLowerCase()),
  );
  if (!shouty) return capFirst(s);
  const lowered = s.replace(/[A-Za-z0-9]+/g, (t) =>
    t === t.toUpperCase() && ACR.has(t.toLowerCase()) ? ACR.get(t.toLowerCase()) : t.toLowerCase(),
  );
  return capFirst(lowered);
};

/** Sentence case for short enum values ("Driver License" → "Driver license"). */
export const sentenceCase = (value) => {
  const s = String(value ?? "").replace(/[_]+/g, " ").trim();
  if (!s) return s;
  const lowered = s.replace(/[A-Za-z0-9]+/g, (t) =>
    ACR.has(t.toLowerCase()) && t === t.toUpperCase() ? ACR.get(t.toLowerCase()) : t.toLowerCase(),
  );
  return capFirst(lowered);
};

// ── legacy labels (what the old FieldsPane / toFieldGroups showed) ───────────
// Kept so `focusField` strings written against the old labels still resolve.
export const legacyHumanize = (k) => {
  const s = String(k)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

/** Normalized match key: lowercase words, non-alphanumerics collapsed. */
export const normKey = (s) =>
  String(s || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const pluralize = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;
